import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { c } from "react-compiler-runtime";
import { ChevronDownIcon } from "@graphiql/react";
import { isUnionType, isInterfaceType } from "graphql";
import { useState, useEffect } from "react";
import { useFieldTreeContext } from "./field-tree-context.js";
import { FieldTreeList } from "./field-tree.js";
import { useCursorReveal } from "./use-cursor-reveal.js";
import { segmentsEqual, inlineFragmentSegment } from "../lib/ast-path.js";
import { isInlineFragmentPresent } from "../lib/inline-fragments.js";
function applicableTypeConditions(abstractType, schema) {
  const objects = schema.getPossibleTypes(abstractType);
  if (!isInterfaceType(abstractType)) {
    return [...objects];
  }
  const skip = /* @__PURE__ */ new Set([abstractType.name, ...abstractType.getInterfaces().map((i) => i.name)]);
  const interfaces = /* @__PURE__ */ new Map();
  for (const object of objects) {
    for (const iface of object.getInterfaces()) {
      if (!skip.has(iface.name)) {
        interfaces.set(iface.name, iface);
      }
    }
  }
  return [...objects, ...interfaces.values()];
}
const TypeConditionSelector = (t0) => {
  var _a;
  const $ = c(58);
  const {
    fieldPath,
    abstractType
  } = t0;
  const {
    doc,
    schema,
    target,
    cursorPath
  } = useFieldTreeContext();
  let t1;
  if ($[0] !== abstractType || $[1] !== doc || $[2] !== fieldPath || $[3] !== schema || $[4] !== target) {
    let t24;
    if ($[6] !== doc || $[7] !== fieldPath || $[8] !== target) {
      t24 = (t) => isInlineFragmentPresent(doc, fieldPath, t.name, target);
      $[6] = doc;
      $[7] = fieldPath;
      $[8] = target;
      $[9] = t24;
    } else {
      t24 = $[9];
    }
    t1 = (schema ? applicableTypeConditions(abstractType, schema) : []).some(t24);
    $[0] = abstractType;
    $[1] = doc;
    $[2] = fieldPath;
    $[3] = schema;
    $[4] = target;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const hasSelectedCondition = t1;
  const [expanded, setExpanded] = useState(hasSelectedCondition);
  let t2;
  let t3;
  if ($[10] !== hasSelectedCondition) {
    t2 = () => {
      if (hasSelectedCondition) {
        setExpanded(true);
      }
    };
    t3 = [hasSelectedCondition];
    $[10] = hasSelectedCondition;
    $[11] = t2;
    $[12] = t3;
  } else {
    t2 = $[11];
    t3 = $[12];
  }
  useEffect(t2, t3);
  let t4;
  if ($[13] !== cursorPath || $[14] !== fieldPath) {
    t4 = cursorPath !== void 0 && cursorPath.length > fieldPath.length && fieldPath.every((seg, i) => segmentsEqual(cursorPath[i], seg)) && ((_a = cursorPath[fieldPath.length]) == null ? void 0 : _a.kind) === "inlineFragment";
    $[13] = cursorPath;
    $[14] = fieldPath;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const cursorInCondition = t4;
  let t5;
  if ($[16] !== cursorInCondition) {
    t5 = () => {
      if (cursorInCondition) {
        setExpanded(true);
      }
    };
    $[16] = cursorInCondition;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== cursorInCondition || $[19] !== cursorPath) {
    t6 = [cursorPath, cursorInCondition];
    $[18] = cursorInCondition;
    $[19] = cursorPath;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  useEffect(t5, t6);
  if (!schema) {
    return null;
  }
  let t10;
  let t11;
  let t7;
  let t8;
  let t9;
  let typeConditions;
  if ($[21] !== abstractType || $[22] !== doc || $[23] !== fieldPath || $[24] !== schema || $[25] !== target) {
    t11 = Symbol.for("react.early_return_sentinel");
    bb0: {
      typeConditions = applicableTypeConditions(abstractType, schema);
      if (typeConditions.length === 0) {
        t11 = null;
        break bb0;
      }
      t7 = "graphiql-qb-type-condition";
      t8 = "group";
      t9 = `Possible types for ${abstractType.name}`;
      let t122;
      if ($[32] !== doc || $[33] !== fieldPath || $[34] !== target) {
        t122 = (conditionType) => {
          const isSelected = isInlineFragmentPresent(doc, fieldPath, conditionType.name, target);
          return /* @__PURE__ */ jsx(TypeConditionEntry, { fieldPath, typeName: conditionType.name, concreteType: conditionType, isInterface: isInterfaceType(conditionType), isSelected }, conditionType.name);
        };
        $[32] = doc;
        $[33] = fieldPath;
        $[34] = target;
        $[35] = t122;
      } else {
        t122 = $[35];
      }
      t10 = typeConditions.map(t122);
    }
    $[21] = abstractType;
    $[22] = doc;
    $[23] = fieldPath;
    $[24] = schema;
    $[25] = target;
    $[26] = t10;
    $[27] = t11;
    $[28] = t7;
    $[29] = t8;
    $[30] = t9;
    $[31] = typeConditions;
  } else {
    t10 = $[26];
    t11 = $[27];
    t7 = $[28];
    t8 = $[29];
    t9 = $[30];
    typeConditions = $[31];
  }
  if (t11 !== Symbol.for("react.early_return_sentinel")) {
    return t11;
  }
  let t12;
  if ($[36] !== t10 || $[37] !== t7 || $[38] !== t8 || $[39] !== t9) {
    t12 = /* @__PURE__ */ jsx("div", { className: t7, role: t8, "aria-label": t9, children: t10 });
    $[36] = t10;
    $[37] = t7;
    $[38] = t8;
    $[39] = t9;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  const entries = t12;
  if (isUnionType(abstractType)) {
    return entries;
  }
  const t13 = `calc(${fieldPath.length * 12}px + var(--px-8))`;
  let t14;
  if ($[41] !== t13) {
    t14 = {
      paddingLeft: t13
    };
    $[41] = t13;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  let t15;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => setExpanded(_temp);
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  const t16 = expanded ? "graphiql-qb-chevron-expanded" : "graphiql-qb-chevron-collapsed";
  let t17;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = /* @__PURE__ */ jsx(ChevronDownIcon, {});
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== t16) {
    t18 = /* @__PURE__ */ jsx("span", { className: t16, children: t17 });
    $[45] = t16;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = /* @__PURE__ */ jsx("span", { className: "graphiql-qb-possible-types-label", children: "Possible types" });
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  let t20;
  if ($[48] !== typeConditions.length) {
    t20 = /* @__PURE__ */ jsx("span", { className: "graphiql-qb-possible-types-count", children: typeConditions.length });
    $[48] = typeConditions.length;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  let t21;
  if ($[50] !== expanded || $[51] !== t14 || $[52] !== t18 || $[53] !== t20) {
    t21 = /* @__PURE__ */ jsxs("button", { type: "button", className: "graphiql-qb-field-toggle graphiql-qb-possible-types-header", style: t14, onClick: t15, "aria-expanded": expanded, children: [
      t18,
      t19,
      t20
    ] });
    $[50] = expanded;
    $[51] = t14;
    $[52] = t18;
    $[53] = t20;
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  const t22 = expanded && entries;
  let t23;
  if ($[55] !== t21 || $[56] !== t22) {
    t23 = /* @__PURE__ */ jsxs("div", { className: "graphiql-qb-possible-types", children: [
      t21,
      t22
    ] });
    $[55] = t21;
    $[56] = t22;
    $[57] = t23;
  } else {
    t23 = $[57];
  }
  return t23;
};
const TypeConditionEntry = (t0) => {
  const $ = c(48);
  const {
    fieldPath,
    typeName,
    concreteType,
    isInterface,
    isSelected
  } = t0;
  const {
    cursorPath,
    onAddInlineFragment,
    onRemoveInlineFragment
  } = useFieldTreeContext();
  const [expanded, setExpanded] = useState(false);
  let t1;
  if ($[0] !== typeName) {
    t1 = inlineFragmentSegment(typeName);
    $[0] = typeName;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== fieldPath || $[3] !== t1) {
    t2 = [...fieldPath, t1];
    $[2] = fieldPath;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const fragmentPath = t2;
  const {
    flash,
    current,
    nodeRef
  } = useCursorReveal(fragmentPath, cursorPath, setExpanded);
  let t3;
  if ($[5] !== fieldPath || $[6] !== isSelected || $[7] !== onAddInlineFragment || $[8] !== onRemoveInlineFragment || $[9] !== typeName) {
    t3 = function handleToggle2() {
      if (isSelected) {
        onRemoveInlineFragment == null ? void 0 : onRemoveInlineFragment(fieldPath, typeName);
      } else {
        onAddInlineFragment == null ? void 0 : onAddInlineFragment(fieldPath, typeName);
      }
    };
    $[5] = fieldPath;
    $[6] = isSelected;
    $[7] = onAddInlineFragment;
    $[8] = onRemoveInlineFragment;
    $[9] = typeName;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const handleToggle = t3;
  const t4 = `graphiql-qb-inline-fragment${flash ? " graphiql-qb-flash" : ""}${current ? " graphiql-qb-current" : ""}`;
  const t5 = `calc(${fieldPath.length * 12}px + var(--px-8))`;
  let t6;
  if ($[11] !== t5) {
    t6 = {
      paddingLeft: t5
    };
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const t7 = `Toggle ... on ${typeName}`;
  let t8;
  if ($[13] !== handleToggle || $[14] !== isSelected || $[15] !== t7) {
    t8 = /* @__PURE__ */ jsx("input", { type: "checkbox", checked: isSelected, onChange: handleToggle, "aria-label": t7, className: "graphiql-qb-field-checkbox" });
    $[13] = handleToggle;
    $[14] = isSelected;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => setExpanded(_temp2);
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const t10 = `${expanded ? "Collapse" : "Expand"} ... on ${typeName}`;
  const t11 = expanded ? "graphiql-qb-chevron-expanded" : "graphiql-qb-chevron-collapsed";
  let t12;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = /* @__PURE__ */ jsx(ChevronDownIcon, {});
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== t11) {
    t13 = /* @__PURE__ */ jsx("span", { className: t11, children: t12 });
    $[19] = t11;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = /* @__PURE__ */ jsx("span", { className: "graphiql-qb-spread", children: "..." });
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== typeName) {
    t15 = /* @__PURE__ */ jsx("span", { className: "graphiql-qb-type-name", children: typeName });
    $[22] = typeName;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== isInterface) {
    t16 = isInterface && /* @__PURE__ */ jsx("span", { className: "graphiql-qb-type-kind-tag", children: "interface" });
    $[24] = isInterface;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] !== t15 || $[27] !== t16) {
    t17 = /* @__PURE__ */ jsxs("span", { className: "graphiql-qb-inline-fragment-label", children: [
      t14,
      " on ",
      t15,
      t16
    ] });
    $[26] = t15;
    $[27] = t16;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] !== expanded || $[30] !== t10 || $[31] !== t13 || $[32] !== t17) {
    t18 = /* @__PURE__ */ jsxs("button", { type: "button", className: "graphiql-qb-field-toggle", onClick: t9, "aria-expanded": expanded, "aria-label": t10, children: [
      t13,
      t17
    ] });
    $[29] = expanded;
    $[30] = t10;
    $[31] = t13;
    $[32] = t17;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== t18 || $[35] !== t6 || $[36] !== t8) {
    t19 = /* @__PURE__ */ jsxs("div", { className: "graphiql-qb-inline-fragment-header", style: t6, children: [
      t8,
      t18
    ] });
    $[34] = t18;
    $[35] = t6;
    $[36] = t8;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  let t20;
  if ($[38] !== concreteType || $[39] !== expanded || $[40] !== fragmentPath || $[41] !== isInterface) {
    t20 = expanded && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(FieldTreeList, { type: concreteType, path: fragmentPath }),
      isInterface && /* @__PURE__ */ jsx(TypeConditionSelector, { fieldPath: fragmentPath, abstractType: concreteType })
    ] });
    $[38] = concreteType;
    $[39] = expanded;
    $[40] = fragmentPath;
    $[41] = isInterface;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== nodeRef || $[44] !== t19 || $[45] !== t20 || $[46] !== t4) {
    t21 = /* @__PURE__ */ jsxs("div", { className: t4, ref: nodeRef, children: [
      t19,
      t20
    ] });
    $[43] = nodeRef;
    $[44] = t19;
    $[45] = t20;
    $[46] = t4;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  return t21;
};
function _temp(prev) {
  return !prev;
}
function _temp2(prev) {
  return !prev;
}
export {
  TypeConditionSelector,
  applicableTypeConditions
};
//# sourceMappingURL=type-condition-selector.js.map
